import store from '@ps/store'
import { ActionParams } from '@ps/workbench/undo-redo/action-history'
import {
  AstActionItem,
  AstConditionItem,
  AstEventItem,
} from '@ps/bridge/qc/scripts/editor'
import qc from 'qc'
import { getVpAstFromSceneNode } from '@ps/bridge/qc/vp'
import iframe from '@ps/bridge/iframe/iframe'
import _, { cloneDeep } from 'lodash'
import { generateUUID } from '@ps/utils/game'
import { hasPlaysmartGlobalVars } from '@ps/store/workbench/visual-program/utils'

export type globalVarItem = {
  id?: string // 兼容旧版本没有id的情况
  globalVarKey: string
  type: string
  value: string | number | boolean
  desc: string
}

export type AddGlobalVarActionValue = {
  newValue: globalVarItem
  oldValue: globalVarItem[]
}

export type DeleteGlobalVarActionValue = {
  newValue: number
  oldValue: globalVarItem[]
}

export type EditGlobalVarActionValue = {
  newValue: {
    index: number
    data: globalVarItem
  }
  oldValue: globalVarItem[]
}

type eventConditionForGlobalVar = {
  astIndex: number
  index: number
  value: AstConditionItem
  sceneId: string
  id: string
}
type eventActionForGlobalVar = {
  astIndex: number
  index: number
  value: AstActionItem
  sceneId: string
  id: string
}
export type eventsForGlobalVar = {
  condition: eventConditionForGlobalVar[]
  action: eventActionForGlobalVar[]
}

export type astsForGlobalVar = {
  sceneNode: qc.Node
  ast: AstEventItem[]
}

const conditionHasGlobalVar = (
  condition: AstConditionItem,
  globalVarKey: string,
): boolean => {
  if (
    condition.type === 'compare' &&
    condition.params?.compareData1?.data === globalVarKey
  ) {
    return true
  }
  if (
    condition.type === 'compare' &&
    condition.params?.compareData2Type === 'globalVar' &&
    // @ts-ignore
    condition.params?.compareData2?.data === globalVarKey
  ) {
    return true
  }
  return false
}

const actionHasGlobalVar = (
  action: AstActionItem,
  globalVarKey: string,
): boolean => {
  if (
    action.param &&
    (action.param?.target?.data === globalVarKey ||
      (action.param.valueType === 'globalVar' &&
        action.param?.value?.data === globalVarKey))
  ) {
    return true
  }
  return false
}

export const getEventsForGlobalVar = (
  globalVarKey: string,
): eventsForGlobalVar => {
  const sceneList = iframe.gamePlay.children
  const ret: eventsForGlobalVar = { condition: [], action: [] }
  sceneList.forEach((sceneNode) => {
    const ast = getVpAstFromSceneNode(sceneNode)
    ast.forEach((astItem, astIndex) => {
      if (Array.isArray(astItem.condition)) {
        astItem.condition.forEach((con, conIndex) => {
          if (conditionHasGlobalVar(con, globalVarKey)) {
            ret.condition.push({
              astIndex: astIndex,
              index: conIndex,
              value: con,
              sceneId: sceneNode.uuid,
              id: astItem.id,
            })
          }
        })
      }
      if (Array.isArray(astItem.action)) {
        astItem.action.forEach((act, actIndex) => {
          if (actionHasGlobalVar(act, globalVarKey)) {
            ret.action.push({
              astIndex,
              index: actIndex,
              value: _.cloneDeep(act),
              sceneId: sceneNode.uuid,
              id: astItem.id,
            })
          }
        })
      }
    })
  })
  return ret
}

export const getAddGlobalVarAction = (
  value: AddGlobalVarActionValue,
): ActionParams => {
  return {
    execute: ({ newValue }: AddGlobalVarActionValue) => {
      const globalVar = store.state.workspace.globalVar.globalVar
      if (hasPlaysmartGlobalVars()) {
        newValue.id = '_' + generateUUID()
      }
      globalVar.push(newValue)
      store.commit('workspace/globalVar/updateGlobalVar', globalVar)
    },
    undo: ({ oldValue }: AddGlobalVarActionValue) => {
      store.commit('workspace/globalVar/updateGlobalVar', _.cloneDeep(oldValue))
    },
    value,
    methodName: 'getAddGlobalVarAction',
  }
}
// 删除全局变量时需要删除事件表相关条件和动作
const deleteEventForGlobalVar = (globalVarKey: string): astsForGlobalVar[] => {
  const gamePlayNode = store.state.workspace.game.gamePlay
  const backupAsts: astsForGlobalVar[] = []
  // 删除所有场景中带有这个全局变量的条件和响应动作
  const sceneList = gamePlayNode.children
  sceneList.forEach((sceneNode: qc.Node) => {
    const vpAst = getVpAstFromSceneNode(sceneNode)
    backupAsts.push({
      sceneNode,
      ast: cloneDeep(vpAst),
    })
    vpAst.forEach((astItem) => {
      let _condition = astItem.condition.filter((con) => {
        return !conditionHasGlobalVar(con, globalVarKey)
      })
      if (_condition.length === 0 && astItem.condition.length > 0) {
        const conditionCompareEmpty = cloneDeep(astItem.condition[0])
        delete conditionCompareEmpty.params
        _condition = [conditionCompareEmpty]
      }
      astItem.condition = _condition
      astItem.action = astItem.action.filter((act) => {
        return !actionHasGlobalVar(act, globalVarKey)
      })
    })
    const ansAst = cloneDeep(vpAst)
    if (
      sceneNode.uuid ===
      store.state.workspace.game.sceneManager.currentSceneNode.uuid
    ) {
      store.dispatch('workspace/game/visualProgram/saveAST', {
        sceneNode,
        ast: ansAst,
      })
    } else {
      store.dispatch('workspace/game/visualProgram/saveVpAst', {
        node: sceneNode,
        ast: ansAst,
      })
    }
  })
  return backupAsts
}
// 修改全局变量的名称联动修改相关条件和动作里的数据
const modifiyEventForGlobalVar = (
  newGlobalVarKey: string,
  oldGlobalVarKey: string,
): astsForGlobalVar[] => {
  const gamePlayNode = store.state.workspace.game.gamePlay
  const backupAsts: astsForGlobalVar[] = []
  // 删除所有场景中带有这个全局变量的条件和响应动作
  const sceneList = gamePlayNode.children
  sceneList.forEach((sceneNode: qc.Node) => {
    const vpAst = getVpAstFromSceneNode(sceneNode)
    backupAsts.push({
      sceneNode,
      ast: cloneDeep(vpAst),
    })
    vpAst.forEach((astItem: AstEventItem) => {
      astItem.condition.forEach((con: AstConditionItem) => {
        if (
          con.params &&
          con.params.compareData1 &&
          con.params.compareData1.data === oldGlobalVarKey
        ) {
          con.params.compareData1.data = newGlobalVarKey
        }
        if (
          con.params &&
          con.params.compareData2 &&
          con.params.compareData2.data === oldGlobalVarKey
        ) {
          con.params.compareData2.data = newGlobalVarKey
        }
      })
      astItem.action.forEach((action: AstActionItem) => {
        if (
          action.param &&
          action.param.target &&
          action.param.target.data === oldGlobalVarKey
        ) {
          action.param.target.data = newGlobalVarKey
        }
        if (
          action.param &&
          action.param.value &&
          action.param.value.data === oldGlobalVarKey
        ) {
          action.param.value.data = newGlobalVarKey
        }
      })
      store.dispatch('workspace/game/visualProgram/updateParamUI')
    })
  })
  return backupAsts
}
// 恢复全局变量时需要恢复事件表里的条件和动作
const recoverEventForGlobalVar = (asts: astsForGlobalVar[]) => {
  asts.forEach((ast) => {
    if (
      ast.sceneNode.uuid ===
      store.state.workspace.game.sceneManager.currentSceneNode.uuid
    ) {
      store.dispatch('workspace/game/visualProgram/saveAST', {
        sceneNode: ast.sceneNode,
        ast: ast.ast,
      })
    } else {
      store.dispatch('workspace/game/visualProgram/saveVpAst', {
        node: ast.sceneNode,
        ast: ast.ast,
      })
    }
  })
}

export const getDeleteGlobalVarAction = (value: DeleteGlobalVarActionValue) => {
  return {
    execute: ({ newValue }: DeleteGlobalVarActionValue) => {
      const globalVar = store.state.workspace.globalVar.globalVar
      const globalVarKey = globalVar[newValue].globalVarKey
      globalVar.splice(newValue, 1)
      store.commit('workspace/globalVar/updateGlobalVar', globalVar)
      const asts = deleteEventForGlobalVar(globalVarKey)
      return { asts }
    },
    undo: (
      { oldValue }: DeleteGlobalVarActionValue,
      response: { asts: astsForGlobalVar[] },
    ) => {
      store.commit('workspace/globalVar/updateGlobalVar', _.cloneDeep(oldValue))
      const asts = response.asts
      recoverEventForGlobalVar(asts)
    },
    value,
    methodName: 'getDeleteGlobalVarAction',
  }
}

export const getEditGlobalVarAction = (value: EditGlobalVarActionValue) => {
  return {
    execute: ({ newValue }: EditGlobalVarActionValue) => {
      const globalVar = store.state.workspace.globalVar.globalVar
      const globalVarKey = globalVar[newValue.index].globalVarKey
      // 修改了变量名或变量类型时需要更新事件表
      let asts
      if (
        newValue.data.globalVarKey !== globalVar[newValue.index].globalVarKey &&
        newValue.data.type === globalVar[newValue.index].type
      ) {
        asts = modifiyEventForGlobalVar(
          newValue.data.globalVarKey,
          globalVar[newValue.index].globalVarKey,
        )
      } else if (newValue.data.type !== globalVar[newValue.index].type) {
        asts = deleteEventForGlobalVar(globalVarKey)
      }
      if (hasPlaysmartGlobalVars()) {
        if (
          newValue.data.globalVarKey === globalVar[newValue.index].globalVarKey
        ) {
          newValue.data.id = globalVar[newValue.index].id
        } else {
          newValue.data.id = '_' + generateUUID()
        }
      }
      globalVar[newValue.index] = newValue.data
      store.commit(
        'workspace/globalVar/updateGlobalVar',
        _.cloneDeep(globalVar),
      )
      return { asts }
    },
    undo: (
      { oldValue }: EditGlobalVarActionValue,
      response: { asts: astsForGlobalVar[] },
    ) => {
      store.commit('workspace/globalVar/updateGlobalVar', _.cloneDeep(oldValue))
      const asts = response.asts
      if (asts) {
        recoverEventForGlobalVar(asts)
      }
    },
    value,
    methodName: 'getEditGlobalVarAction',
  }
}
